import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { ConfigProvider, DatePicker, Input, Popover, Select } from 'antd';
import { DownOutlined, DownloadOutlined, PrinterOutlined, SearchOutlined } from '@ant-design/icons';
import { PDFDownloadLink } from '@react-pdf/renderer';
import dayjs from 'dayjs';
import FabricInvoice from '../components/FabricInvoice/FabricInvoice.jsx';
import printInvoice from '../components/FabricInvoice/printInvoice.js';
import SELLER from '../components/FabricInvoice/seller.js';
import useAxiosClient from '../axios-client.js';
import { useStateContext } from '../contexts/ContextProvider.jsx';
import {
  FilterChip, LoadingRow, MenuList, PageBtn, PanelDrawer, StatTile, SummaryStat,
  filterBtn, ghostBtn, miniLabel,
} from '../components/Stock/stockChrome.jsx';

import { PRIMARY, shellAntdTheme, shellVars, STATUS } from '../shellTokens';

const { RangePicker } = DatePicker;

const PAGE_SIZE_OPTIONS = [15, 30, 50];

/**
 * Named ranges for the picker. The report is read month by month in practice,
 * so "This month" and "Last month" are the two that matter.
 */
const PRESETS = [
  { key: 'thisMonth', label: 'This month', range: () => [dayjs().startOf('month'), dayjs().endOf('month')] },
  { key: 'lastMonth', label: 'Last month', range: () => [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')] },
  { key: 'last90', label: 'Last 90 days', range: () => [dayjs().subtract(90, 'day'), dayjs()] },
  { key: 'all', label: 'All time', range: () => null },
];

const fmtKg = (n) => Number(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtMoney = (n) => Number(n ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
const csvCell = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;

const useIsMobile = () => {
  const [mobile, setMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return mobile;
};

/**
 * Every column is fluid with a floor, so the row fits whatever width the card
 * is given instead of forcing the table to scroll sideways.
 *
 * The two text columns floor at 0 and ellipsis; the rest floor at roughly the
 * widest value they realistically hold ("1,158,099", "Couriar"). The floors sum
 * to ~350px, so the grid only scrolls on a container narrower than that — and
 * below 768px the card list renders instead.
 */
const GRID = [
  'minmax(64px,0.7fr)',  // date
  'minmax(0,2fr)',       // customer
  'minmax(0,2.4fr)',     // fabric
  'minmax(78px,1fr)',    // amount
  'minmax(78px,1fr)',    // received
  'minmax(70px,0.9fr)',  // due
  'minmax(62px,0.8fr)',  // method
].join(' ');

/** Numeric cells: right-aligned, never wrapping, clipped rather than widening. */
const numCell = {
  padding: '11px 8px', textAlign: 'right', font: '600 12.5px monospace',
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
};

/** How many parties the rail lists before deferring to the table. */
const PARTY_LIMIT = 12;

/**
 * The raw-fabric sales report.
 *
 * Reads the SALE half of the fabric outflow ledger: what was billed, what came
 * in, and what is still owed — with the party and payment-method breakdowns
 * that say where the outstanding money actually sits.
 *
 * Fabric only. Finished-goods orders carry no payment tracking yet, so nothing
 * here reflects them.
 */
function FabricSales() {
  const axiosClient = useAxiosClient();
  const { darkMode } = useStateContext();
  const isMobile = useIsMobile();

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);

  const [preset, setPreset] = useState('thisMonth');
  const [rangeMenuOpen, setRangeMenuOpen] = useState(false);
  const [range, setRange] = useState(() => PRESETS[0].range());
  const [partyInput, setPartyInput] = useState('');
  const [party, setParty] = useState('');

  const [detail, setDetail] = useState(null);
  // The list row is enough for the drawer, but not for the invoice: that needs
  // the buyer's address, which only the single-sale endpoint carries. Fetched
  // when the drawer opens so the table's payload stays small.
  const [invoiceSale, setInvoiceSale] = useState(null);

  useEffect(() => {
    if (!detail?.id) {
      setInvoiceSale(null);
      return undefined;
    }

    let live = true;
    setInvoiceSale(null);
    axiosClient.get(`/fabricStock/outflows/${detail.id}`)
      .then((res) => { if (live) setInvoiceSale(res.data.data); })
      .catch(() => { /* the drawer still reads fine; only the invoice button waits */ });

    return () => { live = false; };
  }, [axiosClient, detail?.id]);

  const [printing, setPrinting] = useState(false);

  const handlePrint = async () => {
    if (!invoiceSale || printing) return;
    setPrinting(true);
    try {
      const how = await printInvoice(<FabricInvoice sale={invoiceSale} seller={SELLER} />);
      if (how === 'opened') {
        // The browser would not drive its own print dialog for an embedded PDF.
        toast.info('Invoice opened in a new tab — print it from there.');
      }
    } catch {
      toast.error('Could not prepare the invoice for printing');
    } finally {
      setPrinting(false);
    }
  };

  const fetchSales = useCallback(async (overrides = {}) => {
    const p = overrides.page ?? page;
    const size = overrides.pageSize ?? pageSize;
    const r = 'range' in overrides ? overrides.range : range;
    const partyTerm = 'party' in overrides ? overrides.party : party;

    setLoading(true);
    try {
      const res = await axiosClient.get('/fabricStock/outflows', {
        params: {
          purpose: 'SALE',
          page: p,
          perPage: size,
          from: r?.[0] ? r[0].format('YYYY-MM-DD') : undefined,
          to: r?.[1] ? r[1].format('YYYY-MM-DD') : undefined,
          party: partyTerm || undefined,
        },
      });
      setRows(res.data.data || []);
      setSummary(res.data.summary || null);
      setTotal(res.data.meta?.total || 0);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not load sales');
    } finally {
      setLoading(false);
    }
  }, [axiosClient, page, pageSize, range, party]);

  useEffect(() => { fetchSales(); }, [fetchSales]);

  const applyPreset = (key) => {
    // "Custom" only reveals the picker — it names how the range is being set,
    // not a range of its own, so the one on screen stands until the picker
    // changes it.
    if (key === 'custom') {
      setPreset('custom');
      return;
    }
    const found = PRESETS.find((p) => p.key === key);
    const next = found ? found.range() : null;
    setPreset(key);
    setRange(next);
    setPage(1);
    fetchSales({ range: next, page: 1 });
  };

  const applyRange = (value) => {
    setPreset('custom');
    setRange(value);
    setPage(1);
    fetchSales({ range: value, page: 1 });
  };

  const applyParty = (value) => {
    setParty(value);
    setPage(1);
    fetchSales({ party: value, page: 1 });
  };

  /** Exports the page on screen — the breakdowns below already cover the whole range. */
  const handleExport = () => {
    if (!rows.length) {
      toast.info('Nothing to export');
      return;
    }
    const head = ['Date', 'Customer', 'Phone', 'Fabric', 'Amount', 'Received', 'Due', 'Method'];
    const body = rows.map((r) => [
      r.happenedOn,
      r.customer?.name,
      r.customer?.phone,
      (r.lines || []).map((l) => `${l.fabricType} ${l.fabricColor} ${l.weight}kg`).join(' | '),
      r.amount ?? '',
      r.paidAmount ?? '',
      r.due ?? '',
      r.paymentMethod ?? '',
    ]);
    const csv = [head, ...body].map((line) => line.map(csvCell).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `fabric-sales-${dayjs().format('YYYY-MM-DD')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const rootStyle = shellVars(darkMode);

  const lastPage = Math.max(1, Math.ceil(total / pageSize));

  // The parties actually owing money, worst first — the reason this report
  // exists. Parties that have settled are not the story.
  const owing = useMemo(
    () => (summary?.byParty || []).filter((p) => p.due > 0.5),
    [summary],
  );

  const rangeLabel = range?.[0]
    ? `${range[0].format('D MMM YYYY')} – ${range[1].format('D MMM YYYY')}`
    : 'All time';

  const presetLabel = preset === 'custom'
    ? 'Custom'
    : PRESETS.find((p) => p.key === preset)?.label || 'Range';

  const hasBreakdown = !!summary && (summary.byParty?.length > 0);
  // Parties owing lead; if everyone has settled, the panel falls back to the
  // whole list so it still says who bought. Capped because the rail scrolls
  // but is not the place to read a hundred rows — the table is.
  const rankedParties = owing.length ? owing : (summary?.byParty || []);
  const shownParties = rankedParties.slice(0, PARTY_LIMIT);
  const hiddenParties = Math.max(0, rankedParties.length - shownParties.length);

  return (
    <div className="et-sales" style={rootStyle}>
      <style>{`
        .et-sales{font-family:Outfit,system-ui,sans-serif;color:var(--text);display:flex;flex-direction:column;gap:14px;height:100%;min-height:0}
        .et-sales__row:hover{background:var(--hover)}
        .et-sales__ghost:hover{background:var(--hover)}
        .et-sales__partyrow:hover{background:var(--hover)}

        /* The breakdowns used to sit above the table, as another block in the
           same column — so every party that appeared took height straight off
           the sales list, the one thing on this screen that is meant to fill
           the viewport. They are analysis of the rows, so they belong beside
           them: the order form's rail pattern, at the order form's breakpoint.

           Narrow, there is no room for a rail, so the body scrolls as a page
           and the table takes a bounded height of its own rather than whatever
           the panels leave it. */
        .et-sales__body{flex:1;min-height:0;display:grid;grid-template-columns:minmax(0,1fr);gap:14px;overflow:hidden}
        .et-sales__table{display:flex;flex-direction:column;min-height:0;height:100%;background:var(--surface);border:1px solid var(--border);border-radius:14px;overflow:hidden}
        .et-sales__rail{display:flex;flex-direction:column;gap:14px;min-height:0}
        /* Too narrow for a rail: the panels stack under the table and the body
           scrolls as a page, so the table takes a height of its own instead of
           whatever the panels leave it. Only when there are panels — on their
           own the table still fills the viewport. */
        @media (max-width:1179px){
          .et-sales__body--rail{overflow-y:auto}
          .et-sales__body--rail .et-sales__table{height:min(560px,70vh)}
        }
        @media (min-width:1180px){
          .et-sales__body--rail{grid-template-columns:minmax(0,1fr) 320px}
          /* Only the rail scrolls; the table keeps its own internal scroll. */
          .et-sales__body--rail .et-sales__rail{overflow-y:auto}
        }
      `}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}>
        <div>
          <div style={{ font: '700 22px Outfit, sans-serif', letterSpacing: '-.01em' }}>Fabric Sales</div>
          <div style={{ font: '400 13px Outfit, sans-serif', color: 'var(--muted)', marginTop: 2 }}>
            Raw fabric sold on to customers · {rangeLabel}
          </div>
        </div>
        <button type="button" className="et-sales__ghost" onClick={handleExport} style={ghostBtn}>
          <DownloadOutlined style={{ color: 'var(--muted)', fontSize: 12 }} /> Export
        </button>
      </div>

      <ConfigProvider theme={shellAntdTheme(darkMode)}>
        {/* KPI tiles */}
        {summary && (
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: 14 }}>
            <StatTile
              icon="৳" tint={PRIMARY} label="Billed"
              value={fmtMoney(summary.billed)}
              foot={`${summary.sales} sale${summary.sales === 1 ? '' : 's'}`}
            />
            <StatTile
              icon="✓" tint={STATUS.ok} label="Received"
              value={fmtMoney(summary.paid)}
              foot={summary.billed > 0 ? `${Math.round((summary.paid / summary.billed) * 100)}% collected` : '—'}
            />
            <StatTile
              icon="!" tint={summary.due > 0 ? STATUS.warn : 'var(--muted)'} label="Outstanding"
              value={fmtMoney(summary.due)}
              foot={
                // Across each buyer's whole account, not just the rows in this
                // range: a March bill a June payment cleared is not still owed.
                summary.advance > 0.5
                  ? `${fmtMoney(summary.advance)} held in advance`
                  : owing.length ? `${owing.length} part${owing.length === 1 ? 'y' : 'ies'} owing` : 'all settled'
              }
            />
            <StatTile
              icon="▦" tint="#a78bfa" label="Fabric sold"
              value={`${fmtKg(summary.weight)} kg`}
              foot={`${summary.rolls} roll${summary.rolls === 1 ? '' : 's'}`}
            />
          </div>
        )}

        <div className={`et-sales__body${hasBreakdown ? ' et-sales__body--rail' : ''}`}>
        {/* Table */}
        <div className="et-sales__table">
          {/* Toolbar */}
          <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <div style={{ font: '700 14.5px Outfit, sans-serif' }}>Sales</div>
              <div style={{ font: '600 11.5px monospace', color: 'var(--dim)', padding: '2px 7px', borderRadius: 9, background: 'var(--surface2)' }}>{total}</div>
            </div>
            <div className="et-toolbar">
              {/* The range is picked the way every other filter on the shell is
                  — a button that opens a menu — rather than through an antd
                  Select sitting next to hand-built ones. */}
              <Popover
                trigger="click"
                open={rangeMenuOpen}
                onOpenChange={setRangeMenuOpen}
                content={
                  <MenuList
                    darkMode={darkMode}
                    items={[...PRESETS.map((p) => ({ key: p.key, label: p.label })), { key: 'custom', label: 'Custom range…' }]}
                    active={preset}
                    onSelect={(key) => { setRangeMenuOpen(false); applyPreset(key); }}
                  />
                }
              >
                <button type="button" style={filterBtn}>
                  {presetLabel} <DownOutlined style={{ fontSize: 9, color: 'var(--muted)' }} />
                </button>
              </Popover>
              {/* Only once a custom range is actually being set — otherwise it
                  restates what the button beside it already says. */}
              {preset === 'custom' && (
                <RangePicker
                  value={range}
                  onChange={applyRange}
                  format="D MMM YY"
                  style={{ width: isMobile ? '100%' : 230 }}
                />
              )}
              <Input
                allowClear
                placeholder="Customer or phone"
                prefix={<SearchOutlined style={{ color: 'var(--dim)' }} />}
                style={{ width: isMobile ? '100%' : 180 }}
                value={partyInput}
                onChange={(e) => {
                  setPartyInput(e.target.value);
                  if (e.target.value === '' && party !== '') applyParty('');
                }}
                onPressEnter={() => applyParty(partyInput)}
              />
            </div>
          </div>

          {/* Filter chips — the same row Fabric Store carries, so an active
              filter is stated in one place on both screens. */}
          {(party || preset !== 'thisMonth') && (
            <div style={{ padding: '10px 18px', borderBottom: '1px solid var(--border)', background: 'var(--bg)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ ...miniLabel, marginRight: 2 }}>Filters</span>
              {preset !== 'thisMonth' && (
                <FilterChip label="Range" value={rangeLabel} onClear={() => applyPreset('thisMonth')} />
              )}
              {party && (
                <FilterChip label="Customer" value={party} onClear={() => { setPartyInput(''); applyParty(''); }} />
              )}
            </div>
          )}

          {/* Desktop rows */}
          {!isMobile && (
            <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: GRID, background: 'var(--surface2)', borderBottom: '1px solid var(--border)' }}>
                  {['Date', 'Customer', 'Fabric', 'Amount', 'Received', 'Due', 'Method'].map((h, i) => (
                    <div key={h} style={{
                      padding: i === 0 ? '9px 12px' : '9px 8px', display: 'flex', alignItems: 'center',
                      justifyContent: i >= 3 && i <= 5 ? 'flex-end' : 'flex-start',
                      font: '700 11.5px Outfit, sans-serif', color: 'var(--muted)',
                      letterSpacing: '.05em', textTransform: 'uppercase',
                      // A header that refuses to shrink would reintroduce the
                      // horizontal scroll the fluid grid exists to remove.
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>{h}</div>
                  ))}
                </div>

                {loading && <LoadingRow />}
                {!loading && rows.length === 0 && <EmptyState />}

                {!loading && rows.map((r) => (
                  <div
                    key={r.id}
                    className="et-sales__row"
                    onClick={() => setDetail(r)}
                    style={{ display: 'grid', gridTemplateColumns: GRID, borderBottom: '1px solid var(--border)', alignItems: 'center', cursor: 'pointer' }}
                  >
                    <div style={{ padding: '11px 12px', font: '400 12px monospace', color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.happenedOn ? dayjs(r.happenedOn).format('D MMM') : ''}
                    </div>
                    <div style={{ padding: '11px 8px', minWidth: 0 }}>
                      <div style={{ font: '600 12.5px Outfit, sans-serif', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.customer?.name || '—'}
                      </div>
                      {r.customer?.phone && (
                        <div style={{ font: '400 11px monospace', color: 'var(--dim)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {r.customer.phone}
                        </div>
                      )}
                    </div>
                    <div style={{ padding: '11px 8px', font: '400 12px Outfit, sans-serif', color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {(r.lines || []).map((l) => `${l.fabricType} ${fmtKg(l.weight)}kg`).join(', ') || '—'}
                    </div>
                    <div style={numCell}>{fmtMoney(r.amount)}</div>
                    <div style={{ ...numCell, color: 'var(--muted)' }}>{fmtMoney(r.paidAmount)}</div>
                    <div style={{ ...numCell, fontWeight: 700, color: r.due > 0.5 ? STATUS.warn : STATUS.ok }}>
                      {r.due > 0.5 ? fmtMoney(r.due) : '—'}
                    </div>
                    <div style={{ padding: '11px 12px 11px 8px', font: '400 11.5px Outfit, sans-serif', color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.paymentMethod || '—'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Mobile cards */}
          {isMobile && (
            <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {loading && <LoadingRow />}
              {!loading && rows.length === 0 && <EmptyState />}
              {!loading && rows.map((r) => (
                <div key={r.id} onClick={() => setDetail(r)} style={{ border: '1px solid var(--border)', borderRadius: 12, padding: 12, background: 'var(--surface2)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                    <div>
                      <div style={{ font: '600 13.5px Outfit, sans-serif' }}>{r.customer?.name || '—'}</div>
                      <div style={{ font: '400 11.5px monospace', color: 'var(--muted)', marginTop: 2 }}>
                        {r.happenedOn ? dayjs(r.happenedOn).format('D MMM YYYY') : ''} · {r.paymentMethod || '—'}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ font: '700 14px monospace' }}>{fmtMoney(r.amount)}</div>
                      {r.due > 0.5 && <div style={{ font: '600 11.5px monospace', color: STATUS.warn }}>due {fmtMoney(r.due)}</div>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Footer */}
          <div style={{ padding: '11px 18px', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ font: '400 12px Outfit, sans-serif', color: 'var(--muted)' }}>
              {total} sale{total === 1 ? '' : 's'}
              {summary && <> · billed <span style={{ fontFamily: 'monospace', color: 'var(--text)' }}>{fmtMoney(summary.billed)}</span></>}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Select
                size="small"
                value={pageSize}
                style={{ width: 92 }}
                onChange={(v) => { setPageSize(v); setPage(1); fetchSales({ pageSize: v, page: 1 }); }}
                options={PAGE_SIZE_OPTIONS.map((n) => ({ value: n, label: `${n} / page` }))}
              />
              <PageBtn disabled={page <= 1} onClick={() => { setPage(page - 1); fetchSales({ page: page - 1 }); }}>‹</PageBtn>
              <span style={{ font: '600 12px monospace', color: 'var(--muted)' }}>{page} / {lastPage}</span>
              <PageBtn disabled={page >= lastPage} onClick={() => { setPage(page + 1); fetchSales({ page: page + 1 }); }}>›</PageBtn>
            </div>
          </div>
        </div>

        {/* Breakdowns — the rail. Analysis of the rows beside them, and free to
            grow now that growing costs the table nothing. */}
        {hasBreakdown && (
          <div className="et-sales__rail">
            <Panel title="By party" hint={owing.length ? 'Outstanding first' : 'All settled'}>
              {shownParties.map((p) => (
                <div
                  key={p.party}
                  role="button" tabIndex={0}
                  title={`Filter the table to ${p.party}`}
                  className="et-sales__partyrow"
                  onClick={() => { setPartyInput(p.party); applyParty(p.party); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { setPartyInput(p.party); applyParty(p.party); } }}
                  style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', gap: 10, padding: 8, borderRadius: 8, borderBottom: '1px solid var(--border)', alignItems: 'center', cursor: 'pointer' }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ font: '600 12.5px Outfit, sans-serif', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {p.party}
                    </div>
                    <div style={{ font: '400 11px Outfit, sans-serif', color: 'var(--dim)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {p.phone ? `${p.phone} · ` : ''}{p.orders} sale{p.orders === 1 ? '' : 's'}
                    </div>
                  </div>
                  {/* "1,200 due / of 8,000" labels itself, which a 320px rail
                      has no room to do with a column header. */}
                  <div style={{ textAlign: 'right', flex: 'none' }}>
                    <div style={{ font: '700 12.5px monospace', color: p.due > 0.5 ? STATUS.warn : STATUS.ok }}>
                      {p.due > 0.5 ? `${fmtMoney(p.due)} due` : 'settled'}
                    </div>
                    <div style={{ font: '500 11px monospace', color: 'var(--dim)' }}>of {fmtMoney(p.billed)}</div>
                  </div>
                </div>
              ))}
              {hiddenParties > 0 && (
                <div style={{ padding: '8px 8px 0', font: '400 11.5px Outfit, sans-serif', color: 'var(--dim)' }}>
                  {hiddenParties} more {hiddenParties === 1 ? 'party' : 'parties'} in the table below.
                </div>
              )}
            </Panel>

            <Panel title="By payment method">
              {(summary.byMethod || []).map((m) => {
                const pct = summary.billed > 0 ? (m.billed / summary.billed) * 100 : 0;
                return (
                  <div key={m.method} style={{ padding: '8px 0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                      <span style={{ font: '600 12.5px Outfit, sans-serif' }}>{m.method}</span>
                      <span style={{ font: '600 12.5px monospace', color: 'var(--muted)' }}>{fmtMoney(m.billed)}</span>
                    </div>
                    <div style={{ height: 5, borderRadius: 3, background: 'var(--surface2)', marginTop: 5, overflow: 'hidden' }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: PRIMARY, borderRadius: 3 }} />
                    </div>
                    <div style={{ font: '400 11px Outfit, sans-serif', color: 'var(--dim)', marginTop: 3 }}>
                      {m.orders} sale{m.orders === 1 ? '' : 's'} · {Math.round(pct)}%
                    </div>
                  </div>
                );
              })}
            </Panel>
          </div>
        )}
        </div>

        {/* One sale */}
        <PanelDrawer
          open={!!detail}
          onClose={() => setDetail(null)}
          darkMode={darkMode}
          width={isMobile ? '100%' : 460}
          title={detail?.customer?.name || 'Sale'}
          subtitle={detail?.happenedOn ? dayjs(detail.happenedOn).format('D MMMM YYYY') : undefined}
          bodyPadding={18}
        >
          {detail && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, paddingBottom: 14, borderBottom: '1px solid var(--border)' }}>
                <SummaryStat label="This sale" value={fmtMoney(detail.amount)} tint="var(--text)" />
                <SummaryStat label="Applied" value={fmtMoney(detail.paidAmount)} tint={STATUS.ok} />
                {/* The buyer's balance with this sale counted in — what they
                    owe, rather than what this one document is short. */}
                <SummaryStat
                  label="Balance due"
                  value={detail.balanceDue > 0.5 ? fmtMoney(detail.balanceDue) : '—'}
                  tint={detail.balanceDue > 0.5 ? STATUS.warn : 'var(--muted)'}
                />
              </div>

              {(detail.previousBalance > 0.5 || detail.advance > 0.5) && (
                <div style={{ padding: '10px 0 0', font: '400 12px Outfit, sans-serif', color: 'var(--dim)' }}>
                  {detail.previousBalance > 0.5 && <>Carried {fmtMoney(detail.previousBalance)} from earlier sales. </>}
                  {detail.advance > 0.5 && <>Holds {fmtMoney(detail.advance)} in advance.</>}
                </div>
              )}

              <div style={{ padding: '12px 0', borderBottom: '1px solid var(--border)', font: '400 12.5px Outfit, sans-serif', color: 'var(--muted)' }}>
                {detail.customer?.phone && <>{detail.customer.phone} · </>}
                {detail.paymentMethod || 'method unrecorded'}
                {detail.user && <> · recorded by {detail.user}</>}
              </div>

              <div style={{ paddingTop: 12 }}>
                <div style={{ ...miniLabel, marginBottom: 8 }}>Fabric sold</div>
                {(detail.lines || []).map((l) => (
                  <div key={l.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                    <span style={{ font: '400 12.5px Outfit, sans-serif' }}>{l.fabricType} · {l.fabricColor}</span>
                    <span style={{ font: '600 12.5px monospace', color: 'var(--muted)' }}>{fmtKg(l.weight)} kg · {l.rolls} rolls</span>
                  </div>
                ))}
                <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 10, font: '700 13px monospace' }}>
                  <span>Total</span>
                  <span>{fmtKg(detail.totalWeight)} kg · {detail.totalRolls} rolls</span>
                </div>
              </div>

              {detail.note && (
                <div style={{ marginTop: 14, padding: 10, borderRadius: 8, background: 'var(--surface2)', font: '400 12.5px Outfit, sans-serif', color: 'var(--muted)' }}>
                  {detail.note}
                </div>
              )}

              <div style={{ marginTop: 16, display: 'flex', gap: 10 }}>
                {invoiceSale ? (
                  <>
                    <button
                      type="button"
                      onClick={handlePrint}
                      disabled={printing}
                      style={{ ...ghostBtn, flex: 1, justifyContent: 'center', opacity: printing ? 0.6 : 1 }}
                    >
                      <PrinterOutlined style={{ fontSize: 12 }} />
                      {printing ? 'Preparing…' : 'Print'}
                    </button>
                    <PDFDownloadLink
                      document={<FabricInvoice sale={invoiceSale} seller={SELLER} />}
                      fileName={`${invoiceSale.invoiceNo || `fabric-sale-${invoiceSale.id}`}.pdf`}
                      style={{ textDecoration: 'none', flex: 1 }}
                    >
                      {({ loading: building }) => (
                        <span style={{ ...ghostBtn, width: '100%', justifyContent: 'center' }}>
                          <DownloadOutlined style={{ fontSize: 12 }} />
                          {building ? 'Preparing…' : 'Download'}
                        </span>
                      )}
                    </PDFDownloadLink>
                  </>
                ) : (
                  <span style={{ ...ghostBtn, width: '100%', justifyContent: 'center', opacity: 0.55 }}>
                    <DownloadOutlined style={{ fontSize: 12 }} /> Loading invoice…
                  </span>
                )}
              </div>
              <div style={{ marginTop: 8, font: '400 11.5px Outfit, sans-serif', color: 'var(--dim)', textAlign: 'center' }}>
                Invoice {invoiceSale?.invoiceNo || detail.invoiceNo || '—'}
              </div>
            </>
          )}
        </PanelDrawer>
      </ConfigProvider>
    </div>
  );
}

function Panel({ title, hint, children }) {
  return (
    // flex: none — inside the scrolling rail these must keep their natural
    // height rather than being compressed to fit it.
    <div style={{ flex: 'none', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '12px 14px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ font: '700 13px Outfit, sans-serif' }}>{title}</span>
        {hint && <span style={{ font: '400 11px Outfit, sans-serif', color: 'var(--dim)' }}>{hint}</span>}
      </div>
      {children}
    </div>
  );
}

function EmptyState() {
  return (
    <div style={{ padding: '32px 18px 36px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, textAlign: 'center' }}>
      <span style={{ width: 38, height: 38, borderRadius: 12, background: 'var(--surface2)', color: 'var(--dim)', fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>৳</span>
      <div style={{ font: '600 13.5px Outfit, sans-serif' }}>No sales in this range</div>
      <div style={{ font: '400 12.5px/1.5 Outfit, sans-serif', color: 'var(--muted)', maxWidth: 320 }}>
        Fabric sold to an outside party shows up here. Record one from the Fabric Store.
      </div>
    </div>
  );
}

export default FabricSales;
